"use client";

import { useState, MouseEvent } from "react";
import type { TOCItem, FaqItem } from "@/lib/Types";
import DOMPurify from "isomorphic-dompurify";
type TOCSidebarProps = {
  items: TOCItem[];
  faq: FaqItem[];
};

export default function TOCSidebar({ items, faq }: TOCSidebarProps) {
  const [isOpen, setIsOpen] = useState(false);
  // Track the active index locally to ensure it stays blue after clicking
  const [activeIndex, setActiveIndex] = useState<number | string | null>(null);

  const getFormatId = (title: string) => {
    const cleanStr = title.replace(/<[^>]*>?/gm, "");
    return cleanStr.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  };

  const handleSmoothScroll = (
    event: MouseEvent<HTMLAnchorElement>,
    index: number | string,
  ) => {
    event.preventDefault();
    setActiveIndex(index);

    const targetId =
      index === "faq" ? "faq" : getFormatId(items[index as number].title);
    const targetElement = document.getElementById(targetId);

    if (targetElement) {
      targetElement.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    setIsOpen(false);
  };

  return (
    <>
      {isOpen && (
        <div
          className="[@media(min-width:1366px)]:hidden fixed inset-0 z-50 bg-black/50"
          onClick={() => setIsOpen(false)}
        >
          <div
            className="absolute inset-0 bg-white overflow-y-auto custom-scrollbar"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setIsOpen(false)}
              aria-label="Close modal"
              className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center text-[#00040b] hover:bg-gray-100 rounded-full transition-colors z-10"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>

            {items?.length > 0 && (
              <div className="border border-[#ecf0f6] bg-white h-full">
                <div className="p-4 border-b border-[#ecf0f6]">
                  <h3 className="text-[18px] font-semibold text-[#00040b]">
                    TABLE OF CONTENT
                  </h3>
                </div>
                <div className="p-4 flex flex-col gap-4">
                  <ul className="flex flex-col gap-4">
                    {items?.map((item, idx) => (
                      <li key={idx}>
                        <a
                          onClick={(event) => handleSmoothScroll(event, idx)}
                          href={`#${getFormatId(item.title)}`}
                          className={`text-[16px] leading-[1.32] flex gap-2 ${activeIndex === idx
                              ? "text-[#225bd4] font-semibold"
                              : "text-[#00040b] font-medium"
                            }`}
                        >
                          <span>{String(idx + 1).padStart(2, "0")}.</span>
                          <span
                            dangerouslySetInnerHTML={{
                              __html: DOMPurify.sanitize(item?.title) || "",
                            }}
                          />
                        </a>
                      </li>
                    ))}
                    {faq?.length > 0 && (
                      <li>
                        <a
                          onClick={(event) => handleSmoothScroll(event, "faq")}
                          className={`text-[16px] leading-[1.32] font-medium cursor-pointer flex gap-2 ${activeIndex === "faq"
                              ? "text-[#225bd4] font-semibold"
                              : "text-[#00040b]"
                            }`}
                          href="#faq"
                        >
                          <span>
                            {String(items.length + 1).padStart(2, "0")}.
                          </span>
                          <span>FAQ&apos;s</span>
                        </a>
                      </li>
                    )}
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Desktop Sidebar */}
      {items?.length > 0 && (
        <aside className="hidden lg:block w-full [@media(min-width:1600px)]:w-[552px] lg:w-[100%] 2xl:w-[350px] sticky top-[15px]">
          <div className="border border-[#ecf0f6] bg-white">
            <div className="px-[42px] py-[20px] border-b border-[#ecf0f6]">
              <h3 className="text-[20px] font-semibold text-[#00040b]">
                TABLE OF CONTENT
              </h3>
            </div>
            <div className="p-[42px] flex flex-col gap-[25px] max-h-[calc(100vh-100px)] overflow-y-auto custom-scrollbar">
              {items?.map((item, idx) => (
                <a
                  key={idx}
                  onClick={(event) => handleSmoothScroll(event, idx)}
                  href={`#${getFormatId(item.title)}`}
                  className={`[@media(min-width:1600px)]:text-[20px] text-[16px] leading-[1.32] hover:text-[#225bd4] flex gap-2 transition-colors ${activeIndex === idx
                      ? "text-[#225bd4] font-semibold"
                      : "text-[#00040b] font-medium"
                    }`}
                >
                  <span className="shrink-0">
                    {String(idx + 1).padStart(2, "0")}.
                  </span>
                  <span>{item.title}</span>
                </a>
              ))}
              {faq?.length > 0 && (
                <a
                  onClick={(event) => handleSmoothScroll(event, "faq")}
                  className={`[@media(min-width:1600px)]:text-[20px] text-[16px] leading-[1.32] hover:text-[#225bd4] font-medium cursor-pointer flex gap-2 transition-colors ${activeIndex === "faq"
                      ? "text-[#225bd4] font-semibold"
                      : "text-[#00040b]"
                    }`}
                  href="#faq"
                >
                  <span className="shrink-0">
                    {String(items.length + 1).padStart(2, "0")}.
                  </span>
                  <span>FAQ&apos;s</span>
                </a>
              )}
            </div>
          </div>
        </aside>
      )}
    </>
  );
}
